export type ModerationStatus = {
  configured: boolean;
  connected: boolean;
  broadcasterId: string | null;
  broadcasterName: string | null;
  banScopeReady: boolean;
  chatScopeReady: boolean;
  missingScopes: string[];
  canAuthorise: boolean;
};

export type ModerationConsoleStatus = {
  configured: boolean;
  eventSubConfigured: boolean;
  connected: boolean;
  owner: boolean;
  botModeratorId: string | null;
  botModeratorName: string | null;
  channelControlsReady: boolean;
  shieldReady: boolean;
  warnReady: boolean;
  automodReady: boolean;
  suspiciousReadReady: boolean;
  suspiciousManageReady: boolean;
  moderatorAdminReady: boolean;
  vipAdminReady: boolean;
  missingBotScopes: string[];
  missingBroadcasterAdminScopes: string[];
};

export type ViewerSummary = {
  id: string;
  login: string;
  name: string;
  isBroadcaster: boolean;
  isModerator: boolean;
  isVip: boolean;
  isSubscriber: boolean;
  subscriptionTier: string | null;
};

export type ViewerDetail = {
  viewer: {
    id: string;
    login: string;
    name: string;
    firstSeenAt: string | null;
    firstSeenSource: string | null;
    lastSeenAt: string | null;
    messageCount: number;
    isBroadcaster: boolean;
    isModerator: boolean;
    isVip: boolean;
    isSubscriber: boolean;
    subscriptionTier: string | null;
  };
  moderation: {
    state: 'none' | 'timeout' | 'banned';
    expiresAt: string | null;
    reason: string | null;
  };
  messages: Array<{
    id: string;
    text: string;
    sentAt: string;
    deletedAt: string | null;
  }>;
};

export type ViewerLocalState = {
  notes: Array<{
    id: string;
    text: string;
    actor: string;
    actorRole: string;
    createdAt: string;
    editedBy: string | null;
    editedAt: string | null;
  }>;
  watch: {
    active: boolean;
    reason?: string;
    createdBy?: string;
    createdAt?: string;
    updatedBy?: string;
    updatedAt?: string;
    localOnly: true;
  };
  suspicious: {
    status: string;
    source: string | null;
    authoritativeAt: string | null;
    explanation?: string;
  };
};

export type ChannelState = {
  authoritative: true;
  fetchedAt: string;
  chat: {
    slowMode: boolean;
    slowSeconds: number | null;
    followersOnly: boolean;
    followersMinutes: number | null;
    subscribersOnly: boolean;
    emoteOnly: boolean;
    uniqueChat: boolean;
  };
  shield:
    | { supported: true; active: boolean; lastActivatedAt: string | null; lastModeratorId: string | null; lastModeratorName: string | null }
    | { supported: false; active: null; missingScope: string };
};

export type NeedsAttention = {
  automod: {
    supported: boolean;
    missingScope: string | null;
    messages: Array<{
      messageId: string;
      userId: string;
      login: string;
      name: string;
      text: string;
      reason: 'automod' | 'blocked_term' | null;
      category: string | null;
      level: number | null;
      heldAt: string;
    }>;
  };
  recentFailures: Array<{
    id: string;
    userId: string;
    viewer: string;
    action: string;
    actor: string;
    reason: string | null;
    metadata: Record<string, unknown>;
    createdAt: string;
  }>;
};

export type ActivityEntry = {
  id: string;
  userId: string;
  viewer: string;
  login: string | null;
  action: string;
  origin: 'twitch' | 'ffscontrol-local';
  result: 'success' | 'failure' | 'state';
  actor: string;
  reason: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
};

export type ChannelRoles = {
  moderators: Array<{ user_id?: string; user_login?: string; user_name?: string }>;
  vips: Array<{ user_id?: string; user_login?: string; user_name?: string }>;
  mutationsOwnerOnly: boolean;
  moderatorAdminReady: boolean;
  vipAdminReady: boolean;
};

export type ViewerHistoryResponse = {
  viewers: Array<{
    user_id: string;
    user_login: string;
    display_name: string;
    first_seen_at: string;
    last_seen_at: string;
    message_count: number | string;
    welcome_count: number | string;
    last_welcomed_at: string | null;
  }>;
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
};

export type ChatSearchResponse = {
  messages: Array<{
    message_id: string;
    user_id: string;
    user_login: string;
    user_name: string;
    message_text: string;
    effect_session_id: string | null;
    sent_at: string;
  }>;
  hasMore: boolean;
};

export type ShoutoutSettings = {
  autoRaiders: boolean;
  enabledSince: string | null;
  delaySeconds: number;
  pending: number;
};

export type ShoutoutResult = {
  target: { id: string; login: string; name: string };
  overall: 'completed' | 'failed';
  visual: { status: 'queued' | 'sent' | 'skipped' | 'failed'; message: string | null };
  chat: { status: 'queued' | 'sent' | 'skipped' | 'failed'; message: string | null };
  twitch: { status: 'queued' | 'sent' | 'skipped' | 'failed'; message: string | null };
};

export type AudienceRecords = {
  loyalty: Array<{
    twitchUserId: string;
    login: string | null;
    displayName: string | null;
    balance: number;
  }>;
  raidPreferences: Array<{
    twitchUserId: string;
    login: string | null;
    displayName: string | null;
    preference: string;
    reason: string | null;
  }>;
};

export class ViewerApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ViewerApiError';
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: 'same-origin',
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init?.body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(init?.headers ?? {}),
    },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { message?: string; error?: string };
    throw new ViewerApiError(body.message ?? body.error ?? `Request failed with HTTP ${response.status}.`, response.status);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

function nullableText(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function rowObject(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

export const loadModerationStatus = () => request<ModerationStatus>('/api/v1/moderation/status');

export async function loadModerationConsoleStatus(): Promise<ModerationConsoleStatus> {
  const status = await request<ModerationConsoleStatus>('/api/v1/moderation/console-status');
  if (status.owner) return status;

  /*
   * ViewersPage predates the Lead Moderator role model and uses `owner` only
   * to gate Moderator/VIP mutation buttons and owner-only OAuth prompts.
   * Once the owner has already granted role-admin scopes, expose those
   * operational buttons to the moderator while suppressing credential prompts.
   * The OAuth endpoints themselves remain server-owner-only.
   */
  return {
    ...status,
    owner: status.moderatorAdminReady || status.vipAdminReady,
    missingBotScopes: [],
    missingBroadcasterAdminScopes: [],
  };
}

export const loadCurrentViewers = () => request<{ viewers: ViewerSummary[] }>('/api/v1/moderation/viewers');
export const loadChannelState = () => request<ChannelState>('/api/v1/moderation/channel-state');
export const loadNeedsAttention = () => request<NeedsAttention>('/api/v1/moderation/needs-attention?limit=30');
export const loadModerationActivity = () => request<{ entries: ActivityEntry[] }>('/api/v1/moderation/activity?limit=60');
export const loadChannelRoles = async () => ({
  ...(await request<ChannelRoles>('/api/v1/moderation/channel-roles')),
  mutationsOwnerOnly: false,
});
export const loadViewerDetail = (id: string) => request<ViewerDetail>(`/api/v1/moderation/users/${encodeURIComponent(id)}`);
export const loadViewerLocalState = (id: string) => request<ViewerLocalState>(`/api/v1/moderation/users/${encodeURIComponent(id)}/local`);

export function mutateViewer(path: string, method: 'POST' | 'PUT' | 'PATCH' | 'DELETE' = 'POST', payload?: Record<string, unknown>): Promise<unknown> {
  const operationalPath = path.replace(
    /^\/api\/v1\/moderation\/channel-roles\//,
    '/api/v1/moderation/role-operations/',
  );
  return request(operationalPath, {
    method,
    ...(payload === undefined ? {} : { body: JSON.stringify(payload) }),
  });
}

export function loadViewerHistory(search: string, offset: number): Promise<ViewerHistoryResponse> {
  const params = new URLSearchParams({ search, limit: '25', offset: String(offset) });
  return request(`/api/v1/moderation/history?${params.toString()}`);
}

export function loadChatSearch(search: string, userId: string): Promise<ChatSearchResponse> {
  const params = new URLSearchParams({ limit: '50', offset: '0' });
  if (search.trim()) params.set('search', search.trim());
  if (userId) params.set('userId', userId);
  return request(`/api/v1/moderation/chat-search?${params.toString()}`);
}

export async function loadAudienceRecords(): Promise<AudienceRecords> {
  const catalog = await request<{ loyalty?: unknown[]; raidPreferences?: unknown[] }>('/api/v1/foundation/catalog');
  return {
    loyalty: (catalog.loyalty ?? []).map((value) => {
      const row = rowObject(value);
      return {
        twitchUserId: String(row.twitch_user_id ?? ''),
        login: nullableText(row.login),
        displayName: nullableText(row.display_name),
        balance: Number(row.balance ?? 0),
      };
    }).filter((item) => item.twitchUserId),
    raidPreferences: (catalog.raidPreferences ?? []).map((value) => {
      const row = rowObject(value);
      return {
        twitchUserId: String(row.twitch_user_id ?? ''),
        login: nullableText(row.login),
        displayName: nullableText(row.display_name),
        preference: nullableText(row.preference) ?? 'unknown',
        reason: nullableText(row.reason),
      };
    }).filter((item) => item.twitchUserId),
  };
}

export const loadShoutoutSettings = () => request<ShoutoutSettings>('/api/v1/shoutouts/settings');
export const setAutoShoutouts = (autoRaiders: boolean) => request<ShoutoutSettings>('/api/v1/shoutouts/settings', { method: 'PUT', body: JSON.stringify({ autoRaiders }) });
export const sendManualShoutout = (userId: string, login: string) => request<ShoutoutResult>('/api/v1/shoutouts/manual', { method: 'POST', body: JSON.stringify({ userId, login }) });
